import { useState, useRef } from 'react';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import { uploadFileToServer } from '@/utils/server-upload';
import { useMutation } from '../../content-calendar/hooks/useMutation';

interface BrandProfile {
  name: string;
  slug: string;
  // ... other fields
}

interface ExportData {
  dataUrl: string;
  filename: string;
}

interface UseBrandProfileExportProps {
  brandProfile: BrandProfile;
}

export function useBrandProfileExport({ brandProfile }: UseBrandProfileExportProps) {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogType, setExportDialogType] = useState<'pdf' | 'image' | null>(null);
  const [exportDialogData, setExportDialogData] = useState<ExportData | null>(null);
  const [currentUploadedUrl, setCurrentUploadedUrl] = useState<string | null>(null);

  const brandProfileRef = useRef<HTMLDivElement>(null);

  const prepareBrandProfileClone = async (element: HTMLDivElement): Promise<{ clone: HTMLDivElement; wrapper: HTMLDivElement }> => {
    const clone = element.cloneNode(true) as HTMLDivElement;

    // Remove header/buttons from clone for clean export
    const headerElement = clone.querySelector('[class*="border-b"]');
    if (headerElement) {
      headerElement.remove();
    }

    // Remove height constraints
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';

    // Find and expand all scrollable containers
    const scrollableElements = clone.querySelectorAll('[class*="overflow-y-auto"]') as NodeListOf<HTMLElement>;
    scrollableElements.forEach((el) => {
      el.style.height = 'auto';
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
    });

    // Create wrapper for rendering
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = 'auto';
    wrapper.style.height = 'auto';
    wrapper.style.overflow = 'visible';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '-9999';
    wrapper.style.opacity = '0';

    clone.style.position = 'relative';
    clone.style.top = '0';
    clone.style.left = '0';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    await new Promise(resolve => setTimeout(resolve, 200));

    return { clone, wrapper };
  };

  const exportAsImage = async (element: HTMLDivElement, filename: string): Promise<ExportData> => {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const dataUrl = canvas.toDataURL('image/png');
    return {
      dataUrl,
      filename: `${filename}.png`,
    };
  };

  const exportAsPDF = async (element: HTMLDivElement, filename: string): Promise<ExportData> => {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    // A4 dimensions in points (1 point = 1/72 inch)
    const pdfWidth = 595.28; // A4 width in points
    const pdfHeight = 841.89; // A4 height in points

    // Calculate scaling to fit content on A4
    const margin = 40;
    const contentWidth = pdfWidth - 2 * margin;
    const scaleFactor = contentWidth / imgWidth;
    const contentHeight = imgHeight * scaleFactor;

    // Create PDF with enough pages to fit all content
    const pdf = new jsPDF({
      orientation: contentHeight > pdfWidth - 2 * margin ? 'portrait' : 'portrait',
      unit: 'pt',
      format: 'a4',
    });

    let currentHeight = 0;
    let pageNumber = 1;

    while (currentHeight < contentHeight) {
      if (pageNumber > 1) {
        pdf.addPage();
      }

      const remainingHeight = contentHeight - currentHeight;
      const pageContentHeight = Math.min(pdfHeight - 2 * margin, remainingHeight);

      // Calculate source Y position in the original canvas
      const srcY = (currentHeight / scaleFactor);
      const srcHeight = (pageContentHeight / scaleFactor);

      // Create a temporary canvas for this page's content
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = imgWidth;
      pageCanvas.height = srcHeight;
      const pageCtx = pageCanvas.getContext('2d');

      if (pageCtx) {
        pageCtx.drawImage(canvas, 0, srcY, imgWidth, srcHeight, 0, 0, imgWidth, srcHeight);
        const pageImgData = pageCanvas.toDataURL('image/png');
        pdf.addImage(pageImgData, 'PNG', margin, margin, contentWidth, pageContentHeight);
      }

      currentHeight += pageContentHeight;
      pageNumber++;
    }

    // Add footer with branding
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        'Generated with Kontentino.com',
        pdfWidth / 2,
        pdfHeight - 20,
        { align: 'center' }
      );
    }

    const pdfDataUrl = pdf.output('dataurlstring');
    return {
      dataUrl: pdfDataUrl,
      filename: `${filename}.pdf`,
    };
  };

  const exportImageMutation = useMutation({
    mutationFn: async () => {
      if (!brandProfileRef.current) throw new Error('Brand profile ref not available');
      const { clone, wrapper } = await prepareBrandProfileClone(brandProfileRef.current);
      const filename = `brand-profile-${brandProfile.slug}`;
      const exportData = await exportAsImage(clone, filename);
      document.body.removeChild(wrapper);

      // Upload to server immediately
      try {
        const uploadResult = await uploadFileToServer({
          dataUrl: exportData.dataUrl,
          mimeType: 'image/png',
          filename: exportData.filename,
        });
        return { exportData, uploadUrl: uploadResult.url };
      } catch (error) {
        console.error('Failed to upload PNG to server:', error);
        return { exportData, uploadUrl: null };
      }
    },
    onSuccess: ({ exportData, uploadUrl }) => {
      setCurrentUploadedUrl(uploadUrl);
      setExportDialogType('image');
      setExportDialogData(exportData);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to export as image:', error);
    },
  });

  const handleExportImage = () => {
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    exportImageMutation.mutate();
  };

  const exportPDFMutation = useMutation({
    mutationFn: async () => {
      if (!brandProfileRef.current) throw new Error('Brand profile ref not available');
      const { clone, wrapper } = await prepareBrandProfileClone(brandProfileRef.current);
      const filename = `brand-guidelines-${brandProfile.slug}`;
      const exportData = await exportAsPDF(clone, filename);
      document.body.removeChild(wrapper);

      // Upload to server immediately
      try {
        const uploadResult = await uploadFileToServer({
          dataUrl: exportData.dataUrl,
          mimeType: 'application/pdf',
          filename: exportData.filename,
        });
        return { exportData, uploadUrl: uploadResult.url };
      } catch (error) {
        console.error('Failed to upload PDF to server:', error);
        return { exportData, uploadUrl: null };
      }
    },
    onSuccess: ({ exportData, uploadUrl }) => {
      setCurrentUploadedUrl(uploadUrl);
      setExportDialogType('pdf');
      setExportDialogData(exportData);
      setExportDialogOpen(true);
    },
    onError: (error) => {
      console.error('Failed to export as PDF:', error);
    },
  });

  const handleExportPDF = () => {
    setCurrentUploadedUrl(null);
    setExportDialogData(null);
    setExportDialogType(null);
    exportPDFMutation.mutate();
  };

  const isExporting = exportImageMutation.isLoading || exportPDFMutation.isLoading;

  return {
    isExporting,
    exportDialogOpen,
    setExportDialogOpen,
    exportDialogType,
    exportDialogData,
    currentUploadedUrl,
    brandProfileRef,
    handleExportImage,
    handleExportPDF,
  };
}
